// frontend/src/components/VideoDurationEditor.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { Box, Button, TextField, Typography, CircularProgress, Grid, Alert, Chip, useTheme } from '@mui/material';

// 秒数を「X時間Y分Z秒」形式に変換するヘルパー関数
const formatDurationForDisplay = (secs) => {
  if (secs === null || secs === undefined || isNaN(secs) || secs < 0) return "未設定";
  if (secs === 0) return "0秒";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  let result = "";
  if (h > 0) result += `${h}時間 `;
  if (m > 0 || (h > 0 && s >= 0)) result += `${m}分 `;
  result += `${s}秒`;
  return result.trim() || "0秒";
};

const VideoDurationEditor = ({ videoId, currentDuration, onDurationUpdated }) => {
  const theme = useTheme();
  const [h, setH] = useState('');
  const [m, setM] = useState('');
  const [s, setS] = useState('');
  
  const [isLoadingAPI, setIsLoadingAPI] = useState(false);
  const [isLoadingManual, setIsLoadingManual] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const token = localStorage.getItem("token");

  useEffect(() => {
    if (currentDuration !== null && currentDuration !== undefined && typeof currentDuration === 'number' && currentDuration >= 0) {
      const hours = Math.floor(currentDuration / 3600);
      const minutes = Math.floor((currentDuration % 3600) / 60);
      const seconds = currentDuration % 60;
      setH(String(hours || '0'));
      setM(String(minutes || '0'));
      setS(String(seconds || '0'));
    } else {
      setH('0'); setM('0'); setS('0');
    }
    setMessage(''); 
    setError('');   
  }, [currentDuration]);

  const handleRefreshFromAPI = async () => {
    if (!videoId || !token) {
      setError("エラー: 不正なリクエストです。");
      return;
    }
    setIsLoadingAPI(true);
    setMessage('');
    setError('');
    try {
      const response = await axios.post(`${API_URL}/videos/${videoId}/refresh-duration`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data && response.data.duration !== null && response.data.duration >= 0) {
        setMessage(`APIから長さを再取得・更新しました: ${formatDurationForDisplay(response.data.duration)}`);
        if (onDurationUpdated) {
          onDurationUpdated(videoId, response.data.duration);
        }
      } else {
        setError('APIから有効な動画の長さを取得できませんでした。手動入力を試してください。');
      }
    } catch (err) {
      console.error("Error refreshing duration from API:", err);
      setError(err.response?.data?.detail || 'APIからの長さ再取得に失敗しました。');
    } finally {
      setIsLoadingAPI(false);
    }
  };

  const handleManualSave = async () => {
    if (!videoId || !token) {
        setError("エラー: 不正なリクエストです。");
        return;
    }

    const hours = parseInt(h, 10) || 0;
    const minutes = parseInt(m, 10) || 0;
    const seconds = parseInt(s, 10) || 0;

    if (hours < 0 || minutes < 0 || minutes > 59 || seconds < 0 || seconds > 59) {
      setError('時間(0以上)、分(0-59)、秒(0-59)には正しい数値を入力してください。');
      return;
    }

    const totalSeconds = (hours * 3600) + (minutes * 60) + seconds;

    if (totalSeconds < 0) { 
      setError('合計秒数は0以上の値を入力してください。');
      return;
    }

    setIsLoadingManual(true);
    setMessage('');
    setError('');

    try {
      const response = await axios.patch(`${API_URL}/videos/${videoId}`, 
        { duration: totalSeconds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data && response.data.duration === totalSeconds) {
        setMessage(`動画の長さを手動で更新しました: ${formatDurationForDisplay(totalSeconds)}`);
        if (onDurationUpdated) {
          onDurationUpdated(videoId, totalSeconds);
        }
      } else {
        setError('手動での長さ更新に失敗しました。サーバーの応答を確認してください。');
      }
    } catch (err) {
      console.error("Error saving duration manually:", err);
      setError(err.response?.data?.detail || '手動での長さ更新に失敗しました。');
    } finally {
      setIsLoadingManual(false);
    }
  };
  
  const textColor = theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.text.primary;
  const helperTextColor = theme.palette.mode === 'dark' ? theme.palette.grey[400] : theme.palette.text.secondary;

  return (
    <Box sx={{ 
      mt: 1, 
      mb:1, 
      p: 1.5, 
      border: `1px dashed ${theme.palette.grey[700]}`,
      borderRadius: 1, 
      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0,0,0, 0.03)',
      color: textColor 
    }}>
      <Typography variant="caption" display="block" gutterBottom sx={{color: helperTextColor}}>動画の長さ(Duration)更新:</Typography>
      {message && <Alert severity="success" sx={{mb:1, fontSize: '0.8rem'}}>{message}</Alert>}
      {error && <Alert severity="error" sx={{mb:1, fontSize: '0.8rem'}}>{error}</Alert>}
      
      <Typography variant="body2" sx={{mb:1, fontSize: '0.8rem', color: textColor}}>
        現在の設定: 
        <Chip 
            label={currentDuration !== null && currentDuration !== undefined ? formatDurationForDisplay(currentDuration) : "未設定"} 
            size="small" 
            sx={{
                backgroundColor: (currentDuration === null || currentDuration === undefined || currentDuration <=0) ? theme.palette.grey[700] : theme.palette.success.dark, 
                color: theme.palette.common.white,
                ml: 1
            }}
        />
      </Typography>

      <Button 
        variant="outlined" 
        onClick={handleRefreshFromAPI} 
        disabled={isLoadingAPI || isLoadingManual}
        size="small"
        sx={{mb: 1, mr:1, fontSize: '0.75rem', color: textColor, borderColor: helperTextColor }}
      >
        {isLoadingAPI ? <CircularProgress size={20} color="inherit" /> : "YouTube APIから再取得"}
      </Button>
      
      <Box component="form" onSubmit={(e) => { e.preventDefault(); handleManualSave(); }} noValidate>
        <Grid container spacing={1} alignItems="center" sx={{pl:0.5}}>
          <Grid item xs={2} sm={1.5}> 
            <TextField label="時" type="number" value={h} onChange={(e) => setH(e.target.value)} size="small" fullWidth InputProps={{ inputProps: { min: 0, style: { textAlign: 'right', color: textColor } } }} InputLabelProps={{style: { color: helperTextColor }}} variant="standard" sx={{"& .MuiInput-underline:before": { borderBottomColor: helperTextColor }, "& .MuiInput-underline:hover:not(.Mui-disabled):before": { borderBottomColor: textColor } }}/> 
          </Grid>
          <Grid item xs="auto"><Typography variant="body2" sx={{color: textColor, pt:2}}>時間</Typography></Grid>
          <Grid item xs={2} sm={1.5}> 
            <TextField label="分" type="number" value={m} onChange={(e) => setM(e.target.value)} size="small" fullWidth InputProps={{ inputProps: { min: 0, max: 59, style: { textAlign: 'right', color: textColor } } }} InputLabelProps={{style: { color: helperTextColor }}} variant="standard" sx={{"& .MuiInput-underline:before": { borderBottomColor: helperTextColor }, "& .MuiInput-underline:hover:not(.Mui-disabled):before": { borderBottomColor: textColor } }}/> 
          </Grid>
          <Grid item xs="auto"><Typography variant="body2" sx={{color: textColor, pt:2}}>分</Typography></Grid>
          <Grid item xs={2} sm={1.5}> 
            <TextField label="秒" type="number" value={s} onChange={(e) => setS(e.target.value)} size="small" fullWidth InputProps={{ inputProps: { min: 0, max: 59, style: { textAlign: 'right', color: textColor } } }} InputLabelProps={{style: { color: helperTextColor }}} variant="standard" sx={{"& .MuiInput-underline:before": { borderBottomColor: helperTextColor }, "& .MuiInput-underline:hover:not(.Mui-disabled):before": { borderBottomColor: textColor } }}/> 
          </Grid>
          <Grid item xs="auto"><Typography variant="body2" sx={{color: textColor, pt:2}}>秒</Typography></Grid>
          <Grid item xs={12} sm="auto" sx={{pt: {xs:1, sm:0}, pl: {xs:0, sm:1}}}>
            <Button 
              type="submit"
              variant="contained" 
              color="primary"     
              disabled={isLoadingAPI || isLoadingManual}
              size="small"
              sx={{ fontSize: '0.75rem' }}
            >
              {isLoadingManual ? <CircularProgress size={20} color="inherit" /> : "手動で保存"}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default VideoDurationEditor;